import { Injectable } from '@angular/core';

const HIGHLIGHT_CLASS = 'search-text-highlight';
const HIGHLIGHT_ACTIVE_CLASS = 'search-text-highlight--active';
const HIGHLIGHT_ATTR = 'data-search-highlight';

@Injectable({ providedIn: 'root' })
export class TextHighlightService {
  clearHighlights(container?: HTMLElement): void {
    const root = container ?? document;
    const marks = Array.from(
      root.querySelectorAll<HTMLElement>(`[${HIGHLIGHT_ATTR}]`)
    );
    marks.forEach((mark) => {
      const parent = mark.parentNode;
      if (!parent) return;
      parent.replaceChild(document.createTextNode(mark.textContent ?? ''), mark);
      parent.normalize();
    });
  }
  highlightAndScroll(
    container: HTMLElement,
    query: string,
    scrollContainer: HTMLElement
  ): boolean {
    this.clearHighlights(scrollContainer);
    if (!query.trim()) return false;
    const node = this.findFirstTextNode(container, query);
    if (!node) return false;
    const mark = this.wrapTextNode(node, query);
    if (!mark) return false;
    const containerRect = scrollContainer.getBoundingClientRect();
    const markRect = mark.getBoundingClientRect();
    const PADDING = 80;
    const newScrollTop =
      scrollContainer.scrollTop +
      (markRect.top - containerRect.top) -
      PADDING;
    scrollContainer.scrollTo({ top: newScrollTop, behavior: 'smooth' });
    setTimeout(() => {
      mark.classList.add(HIGHLIGHT_ACTIVE_CLASS);
      setTimeout(() => mark.classList.remove(HIGHLIGHT_ACTIVE_CLASS), 2000);
    }, 400);
    return true;
  }
  private findFirstTextNode(
    root: HTMLElement,
    query: string
  ): Text | null {
    const lowerQuery = query.toLowerCase();
    const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
      acceptNode(node: Node): number {
        const text = node.textContent ?? '';
        if (!text.trim()) return NodeFilter.FILTER_SKIP;
        if ((node.parentElement as HTMLElement)?.hasAttribute(HIGHLIGHT_ATTR))
          return NodeFilter.FILTER_SKIP;
        return text.toLowerCase().includes(lowerQuery)
          ? NodeFilter.FILTER_ACCEPT
          : NodeFilter.FILTER_SKIP;
      },
    });

    return (walker.nextNode() as Text | null);
  }
  private wrapTextNode(node: Text, query: string): HTMLElement | null {
    const text = node.textContent ?? '';
    const lowerText = text.toLowerCase();
    const idx = lowerText.indexOf(query.toLowerCase());
    if (idx === -1) return null;

    const before = text.slice(0, idx);
    const match = text.slice(idx, idx + query.length);
    const after = text.slice(idx + query.length);
    const parent = node.parentNode;
    if (!parent) return null;
    const mark = document.createElement('mark');
    mark.className = `${HIGHLIGHT_CLASS} ${HIGHLIGHT_ACTIVE_CLASS}`;
    mark.setAttribute(HIGHLIGHT_ATTR, 'true');
    mark.textContent = match;
    const fragment = document.createDocumentFragment();
    if (before) fragment.appendChild(document.createTextNode(before));
    fragment.appendChild(mark);
    if (after) fragment.appendChild(document.createTextNode(after));
    parent.replaceChild(fragment, node);
    return mark;
  }
}
